import { Component, ViewChild, ElementRef, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule, NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { FormatService }        from '../../format.service';
import { LoanCalculatorService } from '../../loan-calculator.service';
import { AiAdvisorService }     from '../../ai-advisor.service';
import { ChartService }         from '../../chart.service';
import { ValidationService }    from '../../validation.service';
import {
  AiInsight, LoanSummary, MonthlyPayment,
  Prepayment, ScheduleResult, YearlyPayment,
  PrepaymentFieldError, LOAN_CONSTRAINTS
} from '../../models';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-home-loan-calculator',
  standalone: true,
  imports: [CommonModule, FormsModule, NgClass, RouterLink],
  providers: [ChartService],
  templateUrl: './home-loan-calculator.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./home-loan-calculator.scss']
})
export class HomeLoanCalculator implements OnDestroy {
  fmt = inject(FormatService);
  private calc = inject(LoanCalculatorService);
  private advisor = inject(AiAdvisorService);
  private charts = inject(ChartService);
  private vld = inject(ValidationService);
  private cdr = inject(ChangeDetectorRef);


  @ViewChild('pieChartRef')  pieChartRef!:  ElementRef<HTMLCanvasElement>;
  @ViewChild('barChartRef')  barChartRef!:  ElementRef<HTMLCanvasElement>;
  @ViewChild('lineChartRef') lineChartRef!: ElementRef<HTMLCanvasElement>;

  // ── Expose constants to template ─────────────────────────────────────────
  readonly C = LOAN_CONSTRAINTS;
  readonly Math = Math;
  readonly currentYear = new Date().getFullYear();

  // ── Loan inputs ──────────────────────────────────────────────────────────
  loanAmount = 1_0_000_000;
  annualInterestRate = 8.5;
  loanTermYears = 20;
  loanStartDate:       string = this.currentMonthStr();

  // ── AI Advisor inputs ────────────────────────────────────────────────────
  borrowerAge  = 30;
  monthlyIncome  = 0;
  showAiAdvisor = true;

  // ── Prepayment state ─────────────────────────────────────────────────────
  prepayments:        Prepayment[] = [];
  prepaymentOption:   'reduce_tenure' | 'reduce_emi' = 'reduce_tenure';
  newPrepaymentMonth = 12;
  newPrepaymentAmount = 100_000;
  private nextId = 1;

  // ── Validation ───────────────────────────────────────────────────────────
  formErrors:       Record<string, string> = {};
  prepayErrors:     PrepaymentFieldError   = {};
  touched:          Record<string, boolean>= {};
  prepayTouched:    Record<string, boolean>= {};
  showFormErrors    = false;   // true after first Calculate attempt

  // ── Results ──────────────────────────────────────────────────────────────
  calculated                = false;
  calculating                = false;
  calcError                = false;
  baseSummary:      LoanSummary | null     = null;
  finalSummary:     LoanSummary | null     = null;
  monthlySchedule:  MonthlyPayment[]       = [];
  yearlySchedule:   YearlyPayment[]        = [];
  hasPrepayments                = false;
  aiInsights:       AiInsight[]            = [];
  activeTab:        'yearly' | 'monthly'   = 'yearly';
  expandedYear:     number | null          = null;

  // ── Helpers ───────────────────────────────────────────────────────────────

  private currentMonthStr(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }

  get maxLoanMonths(): number { return Math.floor(this.loanTermYears || 0) * 12; }

  get interestSaved(): number {
    if (!this.baseSummary || !this.finalSummary) return 0;
    return Math.max(0, this.baseSummary.totalInterest - this.finalSummary.totalInterest);
  }

  get monthsSaved(): number {
    if (!this.baseSummary || !this.finalSummary) return 0;
    return Math.max(0, this.baseSummary.totalMonths - this.finalSummary.totalMonths);
  }

  get isFormValid(): boolean {
    return this.validateForm().valid;
  }

  // ── Validation helpers ────────────────────────────────────────────────────

  private validateForm() {
    const result = this.vld.validateLoanForm({
      loanAmount:         this.loanAmount,
      annualInterestRate: this.annualInterestRate,
      loanTermYears:      this.loanTermYears,
      loanStartDate:      this.loanStartDate,
      borrowerAge:        this.borrowerAge,
      monthlyIncome:      this.monthlyIncome
    });
    this.formErrors = result.errors;
    return result;
  }

  /** Called on every input change — live-validates touched fields */
  onFieldChange(field: string): void {
    this.touched[field] = true;
    this.validateForm();
    // If previously calculated and user changes inputs, reset results
    if (this.calculated) {
      this.calculated = false;
      this.charts.destroyAll();
    }
  }

  onFieldBlur(field: string): void {
    this.touched[field] = true;
    // Clamp values on blur
    this.clampField(field);
    this.validateForm();
  }

  private clampField(field: string): void {
    switch (field) {
      case 'loanAmount':
        if (this.loanAmount < 0) this.loanAmount = 0;
        break;
      case 'annualInterestRate':
        if (this.annualInterestRate < 0) this.annualInterestRate = 0;
        if (this.annualInterestRate > this.C.rate.max) this.annualInterestRate = this.C.rate.max;
        break;
      case 'loanTermYears':
        if (this.loanTermYears < 0) this.loanTermYears = 0;
        if (this.loanTermYears > this.C.term.max) this.loanTermYears = this.C.term.max;
        this.loanTermYears = Math.floor(this.loanTermYears);
        // Remove prepayments that now exceed the new tenure
        if (this.prepayments.length) {
          const max = this.maxLoanMonths;
          this.prepayments = this.prepayments.filter(p => p.month <= max);
        }
        break;
      case 'borrowerAge':
        if (this.borrowerAge < 0) this.borrowerAge = 0;
        this.borrowerAge = Math.floor(this.borrowerAge);
        break;
      case 'monthlyIncome':
        if (this.monthlyIncome < 0) this.monthlyIncome = 0;
        break;
    }
  }

  fieldError(field: string): string | null {
    return (this.touched[field] || this.showFormErrors) && this.formErrors[field]
      ? this.formErrors[field]
      : null;
  }

  hasError(field: string): boolean { return !!this.fieldError(field); }

  // ── Prepayment validation ─────────────────────────────────────────────────

  onPrepayFieldChange(field: 'month' | 'amount'): void {
    this.prepayTouched[field] = true;
    this.validatePrepayFields();
  }

  onPrepayFieldBlur(field: 'month' | 'amount'): void {
    this.prepayTouched[field] = true;
    if (field === 'month' && this.newPrepaymentMonth < 0) this.newPrepaymentMonth = 1;
    if (field === 'amount' && this.newPrepaymentAmount < 0) this.newPrepaymentAmount = 0;
    this.validatePrepayFields();
  }

  private validatePrepayFields(): void {
    this.prepayErrors = this.vld.validatePrepayment(
      this.newPrepaymentMonth,
      this.newPrepaymentAmount,
      this.maxLoanMonths || 360
    );
  }

  get prepayFieldsValid(): boolean {
    const e = this.vld.validatePrepayment(
      this.newPrepaymentMonth,
      this.newPrepaymentAmount,
      this.maxLoanMonths || 360
    );
    return Object.keys(e).length === 0;
  }

  prepayError(field: 'month' | 'amount'): string | null {
    return this.prepayTouched[field] && this.prepayErrors[field]
      ? this.prepayErrors[field]!
      : null;
  }

  // ── Prepayment CRUD ───────────────────────────────────────────────────────

  addPrepayment(): void {
    this.prepayTouched = { month: true, amount: true };
    this.validatePrepayFields();
    if (!this.prepayFieldsValid) return;

    const month  = Math.floor(this.newPrepaymentMonth);
    const amount = Math.round(this.newPrepaymentAmount);

    // Merge with existing same-month entry
    const existing = this.prepayments.find(p => p.month === month);
    if (existing) {
      existing.amount += amount;
      this.prepayments = [...this.prepayments];
    } else {
      this.prepayments = [...this.prepayments, { id: this.nextId++, month, amount }]
        .sort((a, b) => a.month - b.month);
    }

    // Reset add form
    this.newPrepaymentMonth  = 12;
    this.newPrepaymentAmount = 100_000;
    this.prepayTouched       = {};
    this.prepayErrors        = {};

    // Recalculate if already shown
    if (this.calculated) this.calculate();
  }

  removePrepayment(id: number): void {
    this.prepayments = this.prepayments.filter(p => p.id !== id);
    if (this.calculated) this.calculate();
  }

  // ── Calculate ─────────────────────────────────────────────────────────────

  calculate(): void {
    this.showFormErrors = true;
    this.touched = {
      loanAmount: true, annualInterestRate: true,
      loanTermYears: true, loanStartDate: true
    };
    const validation = this.validateForm();
    if (!validation.valid) return;

    this.calculating = true;
    this.calcError = false;
    this.cdr.detectChanges();

    try {
      const baseEMI = this.calc.computeEMI(
        this.loanAmount, this.annualInterestRate, this.loanTermYears
      );

      const baseResult: ScheduleResult = this.calc.buildSchedule(
        this.loanAmount, this.annualInterestRate, this.loanTermYears,
        baseEMI, [], this.prepaymentOption, this.loanStartDate
      );
      this.baseSummary = this.calc.buildSummary(baseEMI, baseResult);

      this.hasPrepayments = this.prepayments.length > 0;
      const finalResult: ScheduleResult = this.calc.buildSchedule(
        this.loanAmount, this.annualInterestRate, this.loanTermYears,
        baseEMI, this.prepayments, this.prepaymentOption, this.loanStartDate
      );

      const finalEMI = (this.hasPrepayments && this.prepaymentOption === 'reduce_emi')
        ? finalResult.finalEMI : baseEMI;
      this.finalSummary    = this.calc.buildSummary(finalEMI, finalResult);
      this.monthlySchedule = finalResult.months;
      this.yearlySchedule  = this.calc.buildYearly(finalResult.months);

      this.aiInsights = this.advisor.generate({
        emi:               baseEMI,
        loanTermYears:     this.loanTermYears,
        annualInterestRate:this.annualInterestRate,
        loanAmount:        this.loanAmount,
        totalInterest:     this.baseSummary.totalInterest,
        hasPrepayments:    this.hasPrepayments,
        borrowerAge:       this.borrowerAge || 0,
        monthlyIncome:     this.monthlyIncome || 0,
        baseTotalMonths:   this.baseSummary.totalMonths,
        startDate:         this.loanStartDate,
        prepaymentOption:  this.prepaymentOption
      });

      this.calculated  = true;
      this.calculating = false;
      this.cdr.detectChanges();

      // Render charts after DOM settles
      setTimeout(() => {
        const baseYearly = this.calc.buildYearly(baseResult.months);
        this.charts.render(
          this.pieChartRef.nativeElement,
          this.barChartRef.nativeElement,
          this.lineChartRef.nativeElement,
          {
            loanAmount:     this.loanAmount,
            finalSummary:   this.finalSummary!,
            yearlySchedule: this.yearlySchedule,
            hasPrepayments: this.hasPrepayments,
            monthlySchedule:this.monthlySchedule,
            baseMonths:     this.baseSummary!.totalMonths,
            baseYearly
          }
        );
      }, 80);
    } catch (err) {
      if (!environment.production) {
        console.error('Calculation error', err);
      }
      this.calculating = false;
      this.calcError = true;
    }
  }

  // ── Insight action ────────────────────────────────────────────────────────

  applyInsightAction(insight: AiInsight): void {
    switch (insight.actionKey) {
      case 'setTenure':
        // actionValue is an absolute target year — assign directly, clamped to max
        this.loanTermYears = Math.min(this.C.term.max, insight.actionValue);
        this.touched['loanTermYears'] = true;
        break;
      case 'incrementTenure':
        // actionValue is a delta — add to current tenure, clamped to max
        this.loanTermYears = Math.min(
          this.C.term.max,
          this.loanTermYears + insight.actionValue
        );
        this.touched['loanTermYears'] = true;
        break;
      case 'setRate':
        this.annualInterestRate = insight.actionValue;
        this.touched['annualInterestRate'] = true;
        break;
      case 'setLoanAmount':
        this.loanAmount = insight.actionValue;
        this.touched['loanAmount'] = true;
        break;
      case 'addPrepayment':
        this.newPrepaymentMonth  = insight.actionValue.month;
        this.newPrepaymentAmount = insight.actionValue.amount;
        this.addPrepayment();
        break;
    }
    insight.actionLabel = '✓ Applied';
    insight.actionKey   = undefined;
    this.calculate();
  }

  // ── Table helpers ─────────────────────────────────────────────────────────

  getMonthName(calendarMonth: number): string {
    return this.fmt.monthName(calendarMonth);
  }

  toggleYear(year: number): void {
    this.expandedYear = this.expandedYear === year ? null : year;
  }

  getMonthsForYear(year: number): MonthlyPayment[] {
    return this.monthlySchedule.filter(m => m.calendarYear === year);
  }

  // ── Reset ─────────────────────────────────────────────────────────────────

  reset(): void {
    this.loanAmount         = 1_0_000_000;
    this.annualInterestRate = 8.5;
    this.loanTermYears      = 20;
    this.loanStartDate      = this.currentMonthStr();
    this.borrowerAge        = 30;
    this.monthlyIncome      = 0;
    this.prepayments        = [];
    this.nextId             = 1;
    this.newPrepaymentMonth  = 12;
    this.newPrepaymentAmount = 100_000;
    this.prepaymentOption   = 'reduce_tenure';
    this.formErrors         = {};
    this.prepayErrors       = {};
    this.touched            = {};
    this.prepayTouched      = {};
    this.showFormErrors     = false;
    this.calculated         = false;
    this.calculating        = false;
    this.calcError          = false;
    this.baseSummary        = null;
    this.finalSummary       = null;
    this.monthlySchedule    = [];
    this.yearlySchedule     = [];
    this.aiInsights         = [];
    this.expandedYear       = null;
    this.activeTab          = 'yearly';
    this.charts.destroyAll();
  }

  ngOnDestroy(): void {
    this.charts.destroyAll();
  }
}
